import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Col,
  Descriptions,
  Form,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  theme,
  Typography,
} from "antd";
import { retry } from "../utils/http";
import {
  getCompanies,
  getCompanyAssets,
  getCompanyDepartments,
  getCompanyEmployees,
  getCompanyManagers,
  getTenants,
} from "../repository/data.repository";
import {
  Company,
  CompanyAsset,
  Department,
  EmployeeUser,
  ManagerUser,
  MAX_PAGE_SIZE,
  Tenant,
} from "../repository/data.default";
import {
  QueryOperation,
  SearchQuery,
} from "../code/search-query/search-query";
import { nameOf } from "../utils/typescript";
import CompanyTree from "./CompanyTree";
import { CompanyTreeItem } from "./CompanyTree.default";
import CompanyTreeLegend from "./CompanyTreeLegend";
import DynamicForm from "../controls/DynamicForm";
import { mapCompanyTreeViewQuery } from "./CompanyTreeView.default";

// Both corporate user populations the tree needs to show under a department. The two DTOs share the
// shape the tree reads (uid, name, role, department), so a tagged union keeps per-user navigation and
// the "View Profile" action aware of which list the person belongs to.
type DepartmentUser =
  | ({ readonly __kind: "manager" } & ManagerUser)
  | ({ readonly __kind: "employee" } & EmployeeUser);

function CompanyTreeView() {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<CompanyTreeItem[]>([]);
  const [valuesValid, setValuesValid] = useState(false);
  // Keyed by departmentUid, holds the merged manager+employee population so onExpandRole can filter it
  // without re-hitting the API.
  const employeeCacheRef = useRef<Map<string, DepartmentUser[]>>(new Map());
  const companiesCacheRef = useRef<Map<string, Company[]>>(new Map());
  // The department term as it was when Search was pressed — NOT the live form value. Expanding a company
  // must narrow to the department the user actually searched for; reading the form here would let an
  // unsubmitted edit silently change what an expand returns.
  const appliedDepartmentRef = useRef<string>("");
  // The matching departments per company, from the same fetch that resolved which companies match. Held
  // so a filtered company reports and expands to ITS MATCHES, not to all of its departments — otherwise
  // a company matching on one department would still show a count of eleven and expand to eleven rows.
  const matchedDepartmentsRef = useRef<Map<string, Department[]>>(new Map());

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Leaf user preview — opened when the operator clicks a person in the tree. Pulls everything the
  // modal shows from the data already cached under the department, so no API round trip.
  const [previewUser, setPreviewUser] = useState<DepartmentUser | null>(null);

  // Lifecycle

  // URL-driven preselect: a deep-link to this page can arrive with ?tenantUid / ?companyUid /
  // ?name / ?department set, so an operator can share "the Nuvo structure" or "all departments
  // called Vessel" without walking the admin through the filter form every time. Combobox fields
  // need display+value to render, so we resolve names from the API before applying.
  useEffect(() => {
    const tenantUidParam = searchParams.get("tenantUid") ?? undefined;
    const companyUidParam = searchParams.get("companyUid") ?? undefined;
    const nameParam = searchParams.get("name") ?? undefined;
    const departmentParam = searchParams.get("department") ?? undefined;

    const hasAnyParam =
      !!tenantUidParam || !!companyUidParam || !!nameParam || !!departmentParam;

    if (!hasAnyParam) {
      loadData({ conditions: [] });
      return;
    }

    (async () => {
      const prefill: Record<string, any> = {};

      if (nameParam) prefill.name = nameParam;
      if (departmentParam) prefill.department = departmentParam;

      // Resolve tenant / company display names from the uid so the combobox renders with the
      // right label (and the Clear button later empties it predictably).
      if (tenantUidParam) {
        try {
          const tenantsResult = await retry(() =>
            getTenants({
              searchQuery: {
                conditions: [
                  { field: nameOf<Tenant>("uid"), values: [tenantUidParam], operation: QueryOperation.Equal },
                ],
              },
              pageNumber: 0,
              pageSize: 1,
            }),
          );
          const t = tenantsResult.data[0];
          if (t) prefill.tenant = { display: t.name, value: t.uid };
        } catch {}
      }

      if (companyUidParam) {
        try {
          const companiesResult = await retry(() =>
            getCompanies({
              searchQuery: {
                conditions: [
                  { field: nameOf<Company>("uid"), values: [companyUidParam], operation: QueryOperation.Equal },
                ],
              },
              pageNumber: 0,
              pageSize: 1,
            }),
          );
          const c = companiesResult.data[0];
          if (c) prefill.company = { display: c.name, value: c.uid };
        } catch {}
      }

      form.setFieldsValue(prefill);
      // Pass the ALREADY-RESOLVED prefill through the same mapper the Search button uses so manual
      // search and deep-link search go through one path.
      const searchQuery = mapCompanyTreeViewQuery(prefill as any);
      appliedDepartmentRef.current = String(prefill.department ?? "").trim();
      if (!appliedDepartmentRef.current) {
        loadData(searchQuery);
      } else {
        const byCompany = await resolveDepartmentMatches(appliedDepartmentRef.current);
        matchedDepartmentsRef.current = byCompany;
        const companyUids = Array.from(byCompany.keys());
        if (companyUids.length === 0) {
          setItems([]);
        } else {
          loadData({
            conditions: [
              ...searchQuery.conditions,
              { field: nameOf<Company>("uid"), operation: QueryOperation.In, values: companyUids },
            ],
          });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setValuesValid(!form.getFieldsError().some(({ errors }) => errors.length));
  }, [values]);

  // Events

  // PD-01 §3.4 requires the tree to be searchable by department. `Department` is not a field on Company,
  // so it cannot become a condition on the company query. It is resolved the other way round: find the
  // departments whose name matches, then narrow the companies to the ones that own them. Both endpoints
  // already exist, so this needs no backend change.
  function resolveDepartmentMatches(name: string): Promise<Map<string, Department[]>> {
    return retry(() =>
      getCompanyDepartments({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Department>("name"),
              operation: QueryOperation.StartsWith,
              values: [name],
            },
          ],
        },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    ).then(({ data }) => {
      const byCompany = new Map<string, Department[]>();
      for (const department of data) {
        const existing = byCompany.get(department.companyUid) ?? [];
        existing.push(department);
        byCompany.set(department.companyUid, existing);
      }
      return byCompany;
    });
  }

  async function onSearchData(): Promise<void> {
    employeeCacheRef.current.clear();
    companiesCacheRef.current.clear();
    const searchQuery = mapCompanyTreeViewQuery(values);
    const departmentName = String(values?.department ?? "").trim();
    appliedDepartmentRef.current = departmentName;
    matchedDepartmentsRef.current = new Map();

    if (!departmentName) {
      loadData(searchQuery);
      return;
    }

    setLoading(true);
    try {
      const matches = await resolveDepartmentMatches(departmentName);
      matchedDepartmentsRef.current = matches;
      const companyUids = Array.from(matches.keys());
      // No department matches means no companies match. Showing the unfiltered tree here would read as
      // "your search found everything" — the empty result is the honest answer.
      if (companyUids.length === 0) {
        setItems([]);
        setLoading(false);
        return;
      }
      loadData({
        ...searchQuery,
        conditions: [
          ...searchQuery.conditions,
          {
            field: nameOf<Company>("uid"),
            operation: QueryOperation.In,
            values: companyUids,
          },
        ],
      });
    } catch {
      setItems([]);
      setLoading(false);
    }
  }

  function onClearData(): void {
    employeeCacheRef.current.clear();
    companiesCacheRef.current.clear();
    appliedDepartmentRef.current = "";
    matchedDepartmentsRef.current = new Map();
    setValuesValid(true);
    form.resetFields();
    loadData({ conditions: [] });
  }

  // Methods

  function loadData(searchQuery: SearchQuery): void {
    setLoading(true);
    Promise.all([
      retry(() =>
        getTenants({
          searchQuery: { conditions: [] },
          pageNumber: 0,
          pageSize: MAX_PAGE_SIZE,
        }),
      ),
      retry(() =>
        getCompanies({
          searchQuery,
          pageNumber: 0,
          pageSize: MAX_PAGE_SIZE,
        }),
      ),
    ])
      .then(([tenantsResult, companiesResult]) => {
        setLoading(false);

        // Cache all companies by tenant
        const companiesByTenant = new Map<string, Company[]>();
        for (const company of companiesResult.data) {
          const existing = companiesByTenant.get(company.tenantUid) ?? [];
          existing.push(company);
          companiesByTenant.set(company.tenantUid, existing);
        }
        companiesCacheRef.current = companiesByTenant;

        // Create tenant root nodes (only tenants that have matching companies)
        const items: CompanyTreeItem[] = tenantsResult.data
          .filter((tenant) => companiesByTenant.has(tenant.uid))
          .map((tenant) => ({
            uid: tenant.uid,
            name: tenant.name,
            rank: "",
            nodeType: "tenant" as const,
            expandableCount: companiesByTenant.get(tenant.uid)?.length ?? 0,
            subCompaniesCount: 0,
            departmentsCount: 0,
            dataItem: tenant,
          }));

        setItems(items);
      })
      .catch(() => {
        setLoading(false);
      });
  }

  async function onExpandTenant(
    tenantUid: string,
  ): Promise<CompanyTreeItem[]> {
    const companies = companiesCacheRef.current.get(tenantUid) ?? [];
    return companies.map((company) => ({
      uid: company.uid,
      parentCompanyUid: company.parentCompanyUid ?? undefined,
      parentUid: company.parentCompanyUid ? undefined : tenantUid,
      name: company.name,
      rank: company.domainName,
      nodeType: "company" as const,
      // The node must count what it will actually expand to. Unfiltered that is departments PLUS assets
      // (PD-01 §3.4 wants vessels/sites in the hierarchy, and they hang off the company beside its
      // departments). Under a department search it is the matching departments alone — assets are not
      // departments, so including them would pad a count the search is supposed to narrow.
      expandableCount: appliedDepartmentRef.current
        ? (matchedDepartmentsRef.current.get(company.uid)?.length ?? 0)
        : company.departmentsCount + (company.assetsCount ?? 0),
      subCompaniesCount: company.subCompaniesCount,
      departmentsCount: company.departmentsCount,
      tenantUid,
      dataItem: company,
    }));
  }

  async function onExpandCompany(
    companyUid: string,
  ): Promise<CompanyTreeItem[]> {
    // Under a department search the matching departments are already in hand from the search itself —
    // expand to those, and skip the round trip entirely.
    const departments = appliedDepartmentRef.current
      ? (matchedDepartmentsRef.current.get(companyUid) ?? [])
      : (
          await getCompanyDepartments({
            searchQuery: {
              conditions: [
                {
                  field: nameOf<Department>("companyUid"),
                  values: [companyUid],
                  operation: QueryOperation.Equal,
                },
              ],
            },
            pageNumber: 0,
            pageSize: MAX_PAGE_SIZE,
          })
        ).data;

    const departmentNodes: CompanyTreeItem[] = departments.map((dept) => ({
      uid: dept.uid,
      parentUid: dept.companyUid,
      name: dept.name,
      rank: dept.companyName,
      nodeType: "department" as const,
      expandableCount: Number(dept.usersCount) || 0,
      subCompaniesCount: 0,
      departmentsCount: 0,
      dataItem: dept,
    }));

    // A department search asked about departments — adding the company's vessels and sites underneath
    // would answer a question nobody asked, and inflate a filtered view.
    if (appliedDepartmentRef.current) return departmentNodes;

    // §3.4 asks the tree to reach vessels/sites. They are `CompanyAsset`s, siblings of the departments
    // rather than children of them: an employee is mapped onto an asset, so neither owns the other.
    // Leaves — an asset has nothing beneath it in this hierarchy.
    const assets = await getCompanyAssets({
      searchQuery: {
        conditions: [
          {
            field: nameOf<CompanyAsset>("companyUid"),
            values: [companyUid],
            operation: QueryOperation.Equal,
          },
        ],
      },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
    });

    const assetNodes: CompanyTreeItem[] = assets.data.map((asset) => ({
      uid: asset.uid,
      parentUid: companyUid,
      name: asset.name,
      // The asset's own type is its rank line, so a Vessel reads as a vessel without another legend entry.
      rank: asset.assetSubtypeName ?? asset.assetTypeName,
      nodeType: "asset" as const,
      expandableCount: 0,
      subCompaniesCount: 0,
      departmentsCount: 0,
      dataItem: asset,
    }));

    return [...departmentNodes, ...assetNodes];
  }

  async function onExpandDepartment(
    departmentUid: string,
  ): Promise<CompanyTreeItem[]> {
    // Pull BOTH populations in parallel — the tree is meant to show the whole headcount under a
    // department. The Employees endpoint filters `!IsManager`, so without this call managers would
    // silently drop out of the hierarchy (visible everywhere else, invisible here only).
    const [managersResult, employeesResult] = await Promise.all([
      getCompanyManagers({
        searchQuery: {
          conditions: [
            {
              field: nameOf<ManagerUser>("departmentUid"),
              values: [departmentUid],
              operation: QueryOperation.Equal,
            },
          ],
        },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
      getCompanyEmployees({
        searchQuery: {
          conditions: [
            {
              field: nameOf<EmployeeUser>("departmentUid"),
              values: [departmentUid],
              operation: QueryOperation.Equal,
            },
          ],
        },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    ]);

    const departmentUsers: DepartmentUser[] = [
      ...managersResult.data.map(
        (m) => ({ ...m, __kind: "manager" as const }) as DepartmentUser,
      ),
      ...employeesResult.data.map(
        (e) => ({ ...e, __kind: "employee" as const }) as DepartmentUser,
      ),
    ];
    employeeCacheRef.current.set(departmentUid, departmentUsers);

    const roleGroups = new Map<
      string,
      { roleName: string; users: DepartmentUser[] }
    >();
    for (const user of departmentUsers) {
      if (!user.roleUid) continue;
      const existing = roleGroups.get(user.roleUid);
      if (existing) {
        existing.users.push(user);
      } else {
        roleGroups.set(user.roleUid, {
          roleName: user.roleName ?? "",
          users: [user],
        });
      }
    }

    const departmentName =
      departmentUsers.length > 0 ? (departmentUsers[0].departmentName ?? "") : "";

    return Array.from(roleGroups.entries()).map(
      ([roleUid, { roleName, users: roleUsers }]) => ({
        uid: `${departmentUid}__${roleUid}`,
        parentUid: departmentUid,
        name: roleName,
        rank: departmentName,
        nodeType: "role" as const,
        expandableCount: roleUsers.length,
        subCompaniesCount: 0,
        departmentsCount: 0,
        departmentUid,
        roleUid,
      }),
    );
  }

  async function onExpandRole(
    departmentUid: string,
    roleUid: string,
  ): Promise<CompanyTreeItem[]> {
    const users = employeeCacheRef.current.get(departmentUid) ?? [];
    const roleUsers = users.filter((u) => u.roleUid === roleUid);

    return roleUsers.map((u) => ({
      uid: u.uid,
      parentUid: `${departmentUid}__${roleUid}`,
      name: `${u.firstName} ${u.lastName}`,
      // Rank line shows which app the person signs into — Manager vs Employee — in addition to the role.
      // Same role name can appear in both populations if the client has symmetric manager/employee roles.
      rank: u.__kind === "manager" ? `Manager · ${u.roleName ?? ""}` : `Employee · ${u.roleName ?? ""}`,
      nodeType: "employee" as const,
      expandableCount: 0,
      subCompaniesCount: 0,
      departmentsCount: 0,
      dataItem: u,
    }));
  }

  const tenantsRequest = (search: string) => {
    return retry(() =>
      getTenants({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Tenant>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  const companiesRequest = (search: string) => {
    return retry(() =>
      getCompanies({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Company>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  return (
    <Col className="flex flex-auto flex-col flex-nowrap overflow-hidden">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4 pl-0 pr-0">
        <Typography.Title className="m-0" level={4}>
          Companies Tree
        </Typography.Title>
      </Row>
      <Row className="flex flex-auto flex-row flex-nowrap overflow-hidden">
        {/* Form */}
        <Col
          className="mr-4 flex flex-col border border-solid pl-2 pr-2"
          style={{
            borderColor: token.colorBorder,
            borderRadius: token.borderRadius,
          }}
        >
          <Space className="flex flex-row justify-end pb-2 pt-2">
            <Button disabled={loading} onClick={onClearData}>
              Clear
            </Button>
            <Button
              type="primary"
              loading={loading}
              disabled={!valuesValid}
              onClick={onSearchData}
            >
              Search
            </Button>
          </Space>
          <DynamicForm
            className="w-72 overflow-y-auto"
            form={form}
            schema={{
              groups: [
                {
                  controls: [
                    {
                      type: "combobox",
                      name: "tenant",
                      label: "Tenant",
                      request: tenantsRequest,
                    },
                    {
                      type: "combobox",
                      name: "company",
                      label: "Company",
                      request: companiesRequest,
                    },
                    {
                      type: "textbox",
                      name: "name",
                      label: "Company Name",
                    },
                    {
                      type: "textbox",
                      name: "department",
                      label: "Department",
                    },
                  ],
                },
              ],
            }}
          />
          <CompanyTreeLegend />
        </Col>
        {/* Content */}
        <Col className="flex flex-auto flex-col overflow-hidden">
          {loading && (
            <Col className="flex flex-auto items-center justify-center">
              <Spin size="large" />
            </Col>
          )}
          {!loading && items.length === 0 && (
            <Col className="flex flex-auto items-center justify-center">
              <Typography.Text type="secondary">
                No companies found
              </Typography.Text>
            </Col>
          )}
          {!loading && items.length > 0 && (
            <CompanyTree
              items={items}
              onExpandTenant={onExpandTenant}
              onExpandCompany={onExpandCompany}
              onExpandDepartment={onExpandDepartment}
              onExpandRole={onExpandRole}
              onNodeClick={(item) => {
                // The role-grouped children carry the DepartmentUser we cached under the department.
                const u = item.dataItem as DepartmentUser | undefined;
                if (u?.uid) setPreviewUser(u);
              }}
            />
          )}
        </Col>
      </Row>
      <Modal
        open={!!previewUser}
        onCancel={() => setPreviewUser(null)}
        title={
          previewUser && (
            <Space>
              <span>{`${previewUser.firstName} ${previewUser.lastName}`}</span>
              <Tag color={previewUser.__kind === "manager" ? "blue" : "purple"}>
                {previewUser.__kind === "manager" ? "Manager" : "Employee"}
              </Tag>
            </Space>
          )
        }
        footer={
          <Space>
            <Button onClick={() => setPreviewUser(null)}>Close</Button>
            <Button
              type="primary"
              onClick={() => {
                if (!previewUser) return;
                // Hand off to the users grid the person actually belongs to, so the operator picks up
                // on the familiar page with the full card, edit history, etc. The tree-side preview is
                // deliberately light; depth lives on the dedicated pages.
                const target =
                  previewUser.__kind === "manager"
                    ? "/users/managers"
                    : "/users/employees";
                navigate(target);
                setPreviewUser(null);
              }}
            >
              Open on Users Page
            </Button>
          </Space>
        }
      >
        {previewUser && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="User Name">
              {previewUser.userName}
            </Descriptions.Item>
            <Descriptions.Item label="Role">
              {previewUser.roleName ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Department">
              {previewUser.departmentName ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Company">
              {previewUser.companyName ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {previewUser.email || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {previewUser.phoneNumber || "—"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </Col>
  );
}

export default CompanyTreeView;
